/* eslint-disable no-restricted-globals */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { FlatButton, MenuItem, RaisedButton, SelectField, TextField } from 'material-ui';
import { size, map, mapValues, get, isEmpty, orderBy, last, floor } from 'lodash';
import moment from 'moment';
import pallet from '../../../constants/colors';
import useVacationStatusChecker from '../useVacationStatusChecker';

const errorStyle = {
  color: 'red',
  fontSize: '12px',
  margintop: 20,
};

const inputStyle = { width: 310, textAlign: 'left', marginTop: 10 };
const floatingStyle = { textAlign: 'left', width: 340 };

const DayEntryForm = ({
  defaultValues, selectedEntries, clearAndClose, hrsPerDay, post, patch,
  entryTypes, primaryButton, setPrimaryButton, whichButton, valuesByKey,
  getRemainingHours, entriesByDate, holidayCheckInfo, isEdit,
}) => {
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [inOutTimeError, setInOutTimeError] = useState(false);
  const [moreThan24HoursError, setMoreThan24HoursError] = useState(false);

  const [warningText, setWarningText] = useState('');
  const [warningType, setWarningType] = useState('');
  const [showConfirmButton, setShowConfirmButton] = useState(false);

  const { validateHolidayStatus } = useVacationStatusChecker(holidayCheckInfo);

  const locale = localStorage.language === 'is' ? 'is' : 'en';
  const showInOutInput = get(valuesByKey, ['Time.Use.RegisterClock', 'Value']);

  useEffect(() => {
    setForm(defaultValues);
  }, [defaultValues]);

  const closeModal = () => {
    setShowConfirmButton(false);
    clearAndClose();
  };

  const areErrors = () => {
    setErrors(mapValues(form, v => !v));
    if (!size(get(form, 'date')) || !size(get(form, 'timeIn')) || get(form, 'clockCardEntryTypeID') === null) return true;
    if (inOutTimeError) {
      return true;
    }

    const timeFrom = moment(get(form, 'timeIn'));
    const timeTo = moment(get(form, 'timeOut'));
    const duration = moment.duration(timeTo.diff(timeFrom));
    if (duration.asHours() > 24) {
      setMoreThan24HoursError(true);
      return true;
    }

    return false;
  };

  const reset = () => {
    setWarningText('');
    setWarningType('');
    setInOutTimeError(false);
    setMoreThan24HoursError(false);
    setShowConfirmButton(false);
  };

  const formChanged = (key, value) => {
    reset();
    let current;
    if (key === 'date') {
      const hoursRegistered = hrsPerDay - getRemainingHours(value);
      const remaining = hrsPerDay - hoursRegistered;
      let clockIn = moment(value).set({
        hour: 8, minute: 0, second: 0, millisecond: 0,
      });
      if (hoursRegistered > 0) {
        const employeeEntries = get(entriesByDate, moment(value).format('DD/MM/YY'));
        const lastEntry = get(last(orderBy(employeeEntries, 'ClockOut')), 'ClockOut');
        clockIn = moment(lastEntry);
      }
      const clockOut = moment(clockIn).add(remaining, 'hours');
      current = {
        ...form,
        timeIn: clockIn,
        timeOut: clockOut,
        hours: remaining.toFixed(2),
        date: value,
      };
    } else if (key === 'hours') {
      const hoursRegistered = hrsPerDay - getRemainingHours(get(form, 'date'));

      let clockIn = moment(get(form, 'date')).set({
        hour: 8, minute: 0, second: 0, millisecond: 0,
      });
      if (hoursRegistered > 0 && !isEdit) {
        const employeeEntries = get(entriesByDate, moment(get(form, 'date')).format('DD/MM/YY'));
        const lastEntry = get(last(orderBy(employeeEntries, 'ClockOut')), 'ClockOut');
        clockIn = moment(lastEntry);
      }
      const clockOut = moment(clockIn).add(value, 'hours');


      current = {
        ...form, timeIn: clockIn, timeOut: clockOut, hours: value
      };
    } else {
      current = { ...form, [key]: value };
    }
    setForm(current);
    setErrors({ ...errors, [key]: !value });
  };
  const onPressDay = (day) => {
    reset();
    setPrimaryButton(day);
    if (hrsPerDay > 0.00) {
      const clockIn = moment(get(form, 'date')).set({
        hour: 8, minute: 0, second: 0, millisecond: 0,
      });
      const clockOut = moment(clockIn);
      let hrs = hrsPerDay;
      if (day === 'halfDay') {
        clockOut.add((hrsPerDay / 2).toFixed(2), 'hours');
        hrs = (hrsPerDay / 2).toFixed(2);
      } else {
        clockOut.add(hrsPerDay, 'hours');
      }
      const current = {
        ...form, timeIn: clockIn.format('YYYY-MM-DD HH:mm'), timeOut: clockOut.format('YYYY-MM-DD HH:mm'), hours: hrs
      };
      setForm(current);
    }
  };

  const getDuration = (timeIn, timeOut) => {
    if (timeIn !== null && timeOut !== null) {
      const timeFrom = moment(timeIn);
      const timeTo = moment(timeOut);
      const duration = moment.duration(timeTo.diff(timeFrom));
      const mins = duration.asMinutes();
      let hours = floor(mins / 60);
      let minutes = floor(mins % 60);
      if (duration.seconds() >= 30) {
        minutes += 1;
      }
      const hoursInDigits = duration.asHours().toFixed(2);
      const isClockOutBeforeIn = timeTo.isBefore(timeFrom);

      whichButton(hoursInDigits);

      if (isClockOutBeforeIn) {
        setInOutTimeError(true);
        return '';
      }
      if (isNaN(hours)) { hours = 0; }
      if (isNaN(minutes)) { minutes = 0; }

      return `${hours}h ${minutes}m`;
    }
    return `${0}h ${0}m`;
  };

  const getTime = (ClockIn, ClockOut) => {
    const timeData = {};
    if (get(form, 'ClockIn')) {
      timeData.ClockIn = moment(ClockIn).toISOString();
    }
    if (get(form, 'ClockOut')) {
      timeData.ClockOut = moment(ClockOut).toISOString();
    }
    return timeData;
  };
  const submit = () => {
    if (!areErrors()) {
      setLoading(true);
      const clockIn = moment(get(form, 'timeIn'));
      const clockOut = moment(get(form, 'timeOut'));
      const data = {
        ClockCardEntryTypeID: get(form, 'clockCardEntryTypeID'),
        ClockCardEntryComment: get(form, 'clockCardEntryComment'),
        ClockIn: clockIn.toISOString(),
        ClockOut: clockOut.toISOString(),
      };

      if (isEmpty(selectedEntries)) {
        post(data, closeModal);
        setLoading(false);
      } else {
        Promise.all(map(
          selectedEntries,
          ({ ClockCardEntryID, ClockIn, ClockOut }) => {
            const timeProps = size(selectedEntries) > 1 ? getTime(ClockIn, ClockOut) : {};
            patch({ ...data, ...timeProps, ClockCardEntryID }, ClockCardEntryID, closeModal);
          },
        ))
          .then(() => {
            setLoading(false);
          });
      }
    }
  };

  const validateAndSubmit = async () => {
    if (areErrors()) {
      return;
    }
    setLoading(true);
    const typeId = get(form, 'clockCardEntryTypeID');
    const clockIn = moment(get(form, 'timeIn'));
    const clockOut = moment(get(form, 'timeOut'));
    const entryType = get(entryTypes, typeId);
    const isVacation = get(entryType, 'ClockCardEntryTypeRule') === 'Vacation';

    if (isVacation) {
      let currentHoursRequest;
      if (showInOutInput === 'true') {
        currentHoursRequest = clockOut.diff(clockIn, 'minutes') / 60;
      } else {
        currentHoursRequest = get(form, 'hours');
      }

      const editRecord = selectedEntries.length > 0 ? selectedEntries[0] : null;
      const { text, type } = await validateHolidayStatus(parseFloat(currentHoursRequest), editRecord);
      setWarningText(text);
      setWarningType(type);
      if (type === 'success') {
        submit();
      } else if (type === 'warning') {
        setShowConfirmButton(true);
      }
    } else {
      submit();
    }
    setLoading(false);
  };

  return (
    <div>
      <div style={{ marginTop: 16, display: 'flex', }}>
        <RaisedButton
          label={<FormattedMessage id="time.wholeDay" />}
          primary={primaryButton === 'wholeDay'}
          onClick={() => onPressDay('wholeDay')}
        />
        <RaisedButton
          label={<FormattedMessage id="time.halfDay" />}
          primary={primaryButton === 'halfDay'}
          onClick={() => onPressDay('halfDay')}
        />
      </div>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
      }}
      >
        {showInOutInput === 'true' ? (
          <div>
            <TextField
              id="time"
              type="datetime-local"
              floatingLabelFixed
              floatingLabelText={<FormattedMessage id="time.ClockIn" />}
              errorText={errors.timeIn ? <FormattedMessage id="editPanel.required" /> : ''}
              value={get(form, 'timeIn', null) ? moment(form.timeIn).format('YYYY-MM-DD HH:mm') : null}
              onChange={(event) => { formChanged('timeIn', event.target.value); }}
            />

            <TextField
              id="timeOut"
              type="datetime-local"
              floatingLabelFixed
              floatingLabelText={<FormattedMessage id="time.ClockOut" />}
              value={get(form, 'timeOut', null) ? moment(form.timeOut).format('YYYY-MM-DD HH:mm') : null}
              onChange={(event) => { formChanged('timeOut', event.target.value); }}
            />

            {inOutTimeError && <div style={errorStyle}><FormattedMessage id="time.error.TimeOutBeforeTimeIn" /></div>}
          </div>
        ) : (
          <div>
            <TextField
              id="date"
              type="date"
              floatingLabelFixed
              floatingLabelText={<FormattedMessage id="time.Date" />}
              errorText={errors.date ? <FormattedMessage id="editPanel.required" /> : ''}
              value={get(form, 'date')}
              onChange={(event) => { formChanged('date', event.target.value); }}
            />
            <TextField
              type="number"
              value={get(form, 'hours', 0)}
              onChange={(_, val) => { formChanged('hours', val); }}
              hintStyle={inputStyle}
              style={inputStyle}
              inputStyle={inputStyle}
              floatingLabelStyle={floatingStyle}
              floatingLabelFixed
              errorText={errors.hours ? <FormattedMessage id="editPanel.required" /> : ''}
              floatingLabelText={<FormattedMessage id="time.NumberOfHours" />}
            />
          </div>
        )}
        {moreThan24HoursError ? <div style={errorStyle}><FormattedMessage id="time.error.MoreThan24HoursError" /></div> : ''}
        {(get(form, 'timeOut') !== '' && get(form, 'timeIn') !== '' && inOutTimeError !== true) &&
          <div style={{ marginTop: 10 }}>
            {getDuration(get(form, 'timeIn'), get(form, 'timeOut'))}
          </div>}
      </div>


      <SelectField
        fullWidth
        floatingLabelFixed
        floatingLabelText={<FormattedMessage id="time.Type" />}
        value={get(form, 'clockCardEntryTypeID')}
        onChange={(event, index, value) => { formChanged('clockCardEntryTypeID', value); }}
      >
        {map(entryTypes, ({ ClockCardEntryTypeID, ClockCardEntryTypeName, ClockCardEntryTypeName_EN: NameEnglish }) => (
          <MenuItem key={ClockCardEntryTypeID} value={ClockCardEntryTypeID} primaryText={locale === 'is' ? ClockCardEntryTypeName : NameEnglish} />
        ))}
      </SelectField>
      {errors.clockCardEntryTypeID && <div style={errorStyle}><FormattedMessage id="editPanel.required" /></div>}
      <span style={{ color: warningType === 'error' ? 'red' : pallet.warningColor }}>{warningText && warningText}</span>
      <TextField
        fullWidth
        floatingLabelFixed
        floatingLabelText={<FormattedMessage id="time.Comment" />}
        multiLine
        value={get(form, 'clockCardEntryComment')}
        onChange={(_, comment) => formChanged('clockCardEntryComment', comment)}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
        <FlatButton
          label={<FormattedMessage id="time.cancel" />}
          secondary
          onClick={() => closeModal()}
          disabled={loading}
        />
        {!showConfirmButton ? (
          <RaisedButton
            type="submit"
            label={size(selectedEntries) ? <FormattedMessage id="time.update" /> : <FormattedMessage id="time.create" />}
            primary
            onClick={validateAndSubmit}
            disabled={loading || inOutTimeError || get(errors, 'date', false) === true || get(errors, 'timeIn', false) === true || size(get(form, 'clockCardEntryTypeID')) > 0}
            loading={`${loading}`}
          />
        ) : (
          <RaisedButton
            type="submit"
            label={<FormattedMessage id="alert.ok" />}
            primary
            onClick={submit}
            disabled={loading || inOutTimeError || get(errors, 'date', false) === true || get(errors, 'timeIn', false) === true || size(get(form, 'clockCardEntryTypeID')) > 0}
            loading={`${loading}`}
          />
        )}
      </div>
    </div>
  );
};


DayEntryForm.propTypes = {
  clearAndClose: PropTypes.func.isRequired,
  defaultValues: PropTypes.shape({
    clockCardEntryComment: PropTypes.string,
    clockCardEntryTypeID: PropTypes.number,
    timeIn: PropTypes.string,
    timeOut: PropTypes.string,
  }).isRequired,
  entryTypes: PropTypes.shape({}).isRequired,
  hrsPerDay: PropTypes.string.isRequired,
  patch: PropTypes.func.isRequired,
  post: PropTypes.func.isRequired,
  primaryButton: PropTypes.string.isRequired,
  selectedEntries: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setPrimaryButton: PropTypes.func.isRequired,
  whichButton: PropTypes.func.isRequired,
  valuesByKey: PropTypes.object,
  entriesByDate: PropTypes.shape({}).isRequired,
  getRemainingHours: PropTypes.func.isRequired,
  holidayCheckInfo: PropTypes.shape({}).isRequired,
  isEdit: PropTypes.bool.isRequired,
};

export default DayEntryForm;



// WEBPACK FOOTER //
// ./src/sites/time/components/DayEntryForm.js